import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { geoConicEqualArea, geoMercator, geoPath } from "d3-geo";
import { ALL_MARKERS, REGIONS, PRIORITY_MARKETS, EXTENDED_STATES } from "../data/locations";
import { track } from "../lib/analytics";

// Official-boundary India map. The boundary GeoJSON (Survey of India depiction —
// full territory incl. Gilgit-Baltistan + Aksai Chin, Andaman & Nicobar and
// Lakshadweep at true geographic position) is served as a static asset and
// fetched ON DEMAND when a map mounts — it is NOT bundled into the JS, so it
// never slows page boot. Boundaries AND every marker share ONE d3 conic
// equal-area projection (central meridian 80.5°E, standard parallels 12°N/33°N),
// fitted to the boundary FeatureCollection — no hand-measured grids, no offsets.
const MAP_W = 680;
const MAP_H = 800;

// project([lng, lat]) -> [left%, top%] within the map container (SVG units / viewBox)
const pct = ([x, y]) => [(x / MAP_W) * 100, (y / MAP_H) * 100];

// Mumbai Metropolitan Region inset — Mumbai / Navi Mumbai / Thane genuinely sit
// ~15 km apart, so the main-map dots overlap. A labeled, enlarged inset (its own
// fitted Mercator projection over the same boundary data) keeps each city
// selectable without moving any marker off its true geographic position.
const MMR_IDS = ["mumbai", "navi-mumbai", "thane"];
const MMR_BBOX = [72.72, 18.88, 73.22, 19.38]; // [minLng, minLat, maxLng, maxLat]
const mmrBoxFeature = {
  type: "Feature",
  properties: {},
  geometry: {
    type: "Polygon",
    coordinates: [[ // clockwise ring — d3-geo spherical winding convention
      [MMR_BBOX[0], MMR_BBOX[1]], [MMR_BBOX[0], MMR_BBOX[3]],
      [MMR_BBOX[2], MMR_BBOX[3]], [MMR_BBOX[2], MMR_BBOX[1]],
      [MMR_BBOX[0], MMR_BBOX[1]],
    ]],
  },
};
// inset frame placement in SVG units (empty area top-right of the viewBox)
const INSET = { x: 462, y: 6, w: 212, h: 190, labelH: 20, pad: 10 };

// every projection-derived geometry, computed once when the boundary data arrives
function buildMapData(fc) {
  const projection = geoConicEqualArea().parallels([12, 33]).rotate([-80.5, 0]).fitSize([MAP_W, MAP_H], fc);
  const pathGen = geoPath(projection);
  const statePaths = fc.features.map((f) => ({ name: f.properties.name, d: pathGen(f) }));
  const insetProjection = geoMercator().fitExtent(
    [[INSET.x + INSET.pad, INSET.y + INSET.labelH], [INSET.x + INSET.w - INSET.pad, INSET.y + INSET.h - INSET.pad]],
    mmrBoxFeature
  );
  const insetPathGen = geoPath(insetProjection);
  const insetStatePaths = fc.features.map((f) => ({ name: f.properties.name, d: insetPathGen(f) }));
  // dashed reference box on the main map — all four corners projected (conic skew)
  const c = [
    [MMR_BBOX[0], MMR_BBOX[1]], [MMR_BBOX[2], MMR_BBOX[1]],
    [MMR_BBOX[2], MMR_BBOX[3]], [MMR_BBOX[0], MMR_BBOX[3]],
  ].map((p) => projection(p));
  const x0 = Math.min(...c.map((p) => p[0]));
  const y0 = Math.min(...c.map((p) => p[1]));
  const mmr = { x: x0, y: y0, w: Math.max(...c.map((p) => p[0])) - x0, h: Math.max(...c.map((p) => p[1])) - y0 };
  return { projection, insetProjection, statePaths, insetStatePaths, mmr };
}

let boundaryPromise = null;
function loadBoundary() {
  if (!boundaryPromise) {
    boundaryPromise = fetch("/assets/india-boundary.json")
      .then((r) => { if (!r.ok) throw new Error("map data unavailable"); return r.json(); })
      .then(buildMapData)
      .catch((e) => { boundaryPromise = null; throw e; }); // allow retry
  }
  return boundaryPromise;
}

const INSET_MARKERS = ALL_MARKERS.filter((m) => MMR_IDS.includes(m.id));
const MAIN_MARKERS = ALL_MARKERS.filter((m) => !MMR_IDS.includes(m.id) || m.id === "mumbai");
// label placement inside the inset (anchor + dx/dy) — layout-only, dots stay projected
const INSET_LABEL = { mumbai: ["end", -7, 4], "navi-mumbai": ["start", 7, 10], thane: ["start", 7, -4] };

// Framed India map with interactive markers; the selected-market card sits
// beside the map on desktop and below it on mobile (never covering markers).
// Selection state can be controlled by the parent (so market rows beside the map
// can drive it) or falls back to internal state.
export function IndiaMap({ dark = false, active: controlledActive, onActiveChange, placement = "map_page", showLabels = false }) {
  const [internal, setInternal] = useState(ALL_MARKERS[0]);
  const active = controlledActive ?? internal;
  const setActive = onActiveChange ?? setInternal;
  const reduce = useReducedMotion();

  // boundary data loads on demand (static asset fetch, cached module-wide)
  const [mapData, setMapData] = useState(null);
  const [mapError, setMapError] = useState(false);
  useEffect(() => {
    let alive = true;
    loadBoundary()
      .then((d) => { if (alive) setMapData(d); })
      .catch(() => { if (alive) setMapError(true); });
    return () => { alive = false; };
  }, []);

  const activeHasPage = PRIORITY_MARKETS.includes(active.id);
  const landFill = dark ? "fill-zinc-800" : "fill-zinc-200";
  const landStroke = dark ? "stroke-zinc-600" : "stroke-zinc-400";

  const renderMarker = (m, i, positioner, idPrefix = "map-marker", pad = "p-[19px]") => {
    const [left, top] = positioner(m);
    const isActive = active.id === m.id;
    return (
      <motion.button
        key={`${idPrefix}-${m.id}`}
        type="button"
        onClick={() => { setActive(m); track("location_selected", { location: m.id, placement }); }}
        data-testid={`${idPrefix}-${m.id}`}
        aria-label={`${m.name}, ${m.state}`}
        aria-pressed={isActive}
        className={`group absolute -translate-x-1/2 -translate-y-1/2 ${pad}`}
        style={{ left: `${left}%`, top: `${top}%`, zIndex: isActive ? 10 : 1 }}
        initial={reduce ? false : { scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.7 + i * 0.05, type: "spring", stiffness: 220 }}
      >
        <span
          className={`block rounded-full transition-colors duration-200 ${
            isActive
              ? "h-3 w-3 bg-dcc ring-4 ring-dcc/25"
              : `h-2 w-2 ${dark ? "bg-zinc-100" : "bg-charcoal"} group-hover:bg-dcc`
          }`}
        />
      </motion.button>
    );
  };

  return (
    <div data-testid="india-map-block">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        {/* map frame */}
        <div className={`min-w-0 flex-1 border p-3 sm:p-4 ${dark ? "border-zinc-800 bg-zinc-950/40" : "border-zinc-200 bg-white"}`}>
          <div className="relative mx-auto w-full max-w-[560px]" style={{ aspectRatio: `${MAP_W} / ${MAP_H}` }}>
            {!mapData && (
              <div className="absolute inset-0 grid place-items-center" data-testid="map-loading">
                <p className={`animate-pulse font-mono text-[10px] uppercase tracking-[0.3em] ${dark ? "text-zinc-600" : "text-zinc-400"}`}>
                  {mapError ? "Map unavailable — browse the market list" : "Loading map…"}
                </p>
              </div>
            )}
            {mapData && (
            <>
            <motion.svg
              viewBox={`0 0 ${MAP_W} ${MAP_H}`}
              role="img"
              aria-label="Map of India with official national boundaries, showing DCC operating markets including the Andaman & Nicobar and Lakshadweep islands"
              className="block w-full"
              initial={reduce ? false : { opacity: 0, scale: 0.985 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <defs>
                <clipPath id="mmr-inset-clip">
                  <rect x={INSET.x + 1} y={INSET.y + 1} width={INSET.w - 2} height={INSET.h - 2} />
                </clipPath>
              </defs>

              {/* state boundaries — same projection as every marker */}
              <g>
                {mapData.statePaths.map((s) => (
                  <path key={s.name} d={s.d} className={`${landFill} ${landStroke}`} strokeWidth="0.7" />
                ))}
              </g>

              {/* MMR reference box on the main map + connector to the inset */}
              <rect
                x={mapData.mmr.x} y={mapData.mmr.y}
                width={mapData.mmr.w} height={mapData.mmr.h}
                fill="none" className="stroke-dcc/70" strokeWidth="0.8" strokeDasharray="3 2"
              />
              <line
                x1={mapData.mmr.x + mapData.mmr.w} y1={mapData.mmr.y} x2={INSET.x} y2={INSET.y + INSET.h}
                className={dark ? "stroke-zinc-600" : "stroke-zinc-400"} strokeWidth="0.6" strokeDasharray="3 3"
              />

              {/* Mumbai Metropolitan Region inset */}
              <g data-testid="mmr-inset">
                <rect
                  x={INSET.x} y={INSET.y} width={INSET.w} height={INSET.h}
                  className={`${dark ? "fill-zinc-950/90 stroke-zinc-600" : "fill-white/95 stroke-zinc-400"}`} strokeWidth="0.8"
                />
                <g clipPath="url(#mmr-inset-clip)">
                  {mapData.insetStatePaths.map((s) => (
                    <path key={s.name} d={s.d} className={`${landFill} ${landStroke}`} strokeWidth="0.5" />
                  ))}
                </g>
                <text
                  x={INSET.x + 6} y={INSET.y + 13}
                  fill="currentColor" className={dark ? "text-zinc-400" : "text-zinc-500"}
                  fontSize="9" fontFamily="ui-monospace, monospace" letterSpacing="1.5"
                >
                  MUMBAI METRO · ENLARGED
                </text>
                {INSET_MARKERS.map((m) => {
                  const [sx, sy] = mapData.insetProjection([m.lng, m.lat]);
                  const [anchor, dx, dy] = INSET_LABEL[m.id];
                  const isActive = active.id === m.id;
                  return (
                    <text
                      key={m.id}
                      x={sx + dx} y={sy + dy}
                      textAnchor={anchor} fill="currentColor"
                      className={isActive ? "text-dcc" : dark ? "text-zinc-300" : "text-zinc-600"}
                      fontSize="10" fontFamily="ui-monospace, monospace"
                    >
                      {m.name}
                    </text>
                  );
                })}
              </g>

              {/* temporary verification labels (showLabels) — every main-map city */}
              {showLabels && MAIN_MARKERS.map((m) => {
                const [x, y] = mapData.projection([m.lng, m.lat]);
                return (
                  <text key={m.id} x={x + 6} y={y + 3} fill="currentColor" className="text-zinc-300" fontSize="9" fontFamily="ui-monospace, monospace">
                    {m.name}
                  </text>
                );
              })}
            </motion.svg>

            {/* markers — HTML buttons over the shared-projection SVG (keyboard/touch accessible) */}
            {MAIN_MARKERS.map((m, i) => renderMarker(m, i, (mm) => pct(mapData.projection([mm.lng, mm.lat]))))}
            {INSET_MARKERS.map((m, i) => renderMarker(m, MAIN_MARKERS.length + i, (mm) => pct(mapData.insetProjection([mm.lng, mm.lat])), "map-marker-inset", "p-[12px]"))}
            </>
            )}
          </div>

          <p className={`mt-3 font-mono text-[10px] uppercase tracking-[0.25em] sm:text-xs ${dark ? "text-zinc-400" : "text-zinc-600"}`} data-testid="map-coverage-totals">
            DCC client records · <span className={dark ? "text-dcc" : "text-dcc-strong"}>1000+</span> clients served across India · {ALL_MARKERS.length} operating &amp; service markets
          </p>
          <p className={`mt-1.5 font-mono text-[9px] uppercase tracking-[0.2em] ${dark ? "text-zinc-500" : "text-zinc-500"}`}>
            Map depicts the official Government of India boundary · Andaman &amp; Nicobar and Lakshadweep shown at true geographic position
          </p>
        </div>

        {/* selected-market card — beside the map on desktop, below on mobile */}
        <aside
          data-testid="map-detail-panel"
          aria-live="polite"
          className={`w-full shrink-0 border p-4 shadow-2xl sm:p-5 xl:w-[248px] ${dark ? "border-dcc/60 bg-charcoal/95" : "border-dcc/60 bg-white/95"}`}
        >
          <motion.div
            key={active.id}
            initial={reduce ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${dark ? "text-zinc-400" : "text-zinc-500"}`}>{active.state}</p>
            <p className={`h-display mt-1 text-base sm:text-xl ${dark ? "text-zinc-50" : "text-charcoal"}`}>{active.name}</p>
            {active.properties ? (
              <p className="mt-1.5 flex items-baseline gap-2 sm:mt-2" data-testid="map-detail-properties">
                <span className="font-display text-xl font-bold text-dcc sm:text-3xl">{active.properties}</span>
                <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${dark ? "text-zinc-400" : "text-zinc-500"}`}>Properties</span>
              </p>
            ) : (
              <p className={`mt-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] ${dark ? "text-zinc-400" : "text-zinc-500"}`} data-testid="map-detail-properties">
                Programme market · mobilised on schedule
              </p>
            )}
            <Link
              to={activeHasPage ? `/locations/${active.id}` : "/locations"}
              data-testid="map-view-location"
              onClick={() => track("view_location", { location: active.id, placement })}
              className={`link-tech mt-2.5 inline-flex items-center gap-1 text-[11px] ${dark ? "text-dcc" : "text-dcc-strong"}`}
            >
              View location <ArrowUpRight size={12} aria-hidden="true" />
            </Link>
          </motion.div>
        </aside>
      </div>
    </div>
  );
}

// Priority-market rows — real crawlable links to location pages that also drive
// the map selection on hover/focus. Active row highlights in DCC orange.
export function MarketRows({ markets, activeId, onHover, dark = true, testidPrefix = "market-row" }) {
  return (
    <div data-testid={`${testidPrefix}-list`}>
      {markets.map((m) => {
        const isActive = m.id === activeId;
        return (
          <Link
            key={m.id}
            to={`/locations/${m.id}`}
            onMouseEnter={() => onHover?.(m)}
            onFocus={() => onHover?.(m)}
            data-testid={`${testidPrefix}-${m.id}`}
            aria-current={isActive ? "true" : undefined}
            className={`group flex min-h-[52px] items-center justify-between gap-4 border-b py-3 transition-colors ${dark ? "border-zinc-800" : "border-zinc-200"}`}
          >
            <span className="flex min-w-0 flex-wrap items-baseline gap-x-3">
              <span
                className={`inline-block h-2 w-2 shrink-0 transition-colors ${isActive ? "bg-dcc" : dark ? "bg-zinc-600 group-hover:bg-dcc" : "bg-zinc-300 group-hover:bg-dcc"}`}
                aria-hidden="true"
              />
              <span className={`whitespace-nowrap font-display text-xl font-semibold uppercase tracking-tight transition-colors ${isActive ? "text-dcc" : dark ? "text-zinc-100 group-hover:text-dcc" : "text-charcoal group-hover:text-dcc-strong"}`}>
                {m.name}
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{m.state}</span>
            </span>
            <span className={`shrink-0 whitespace-nowrap text-right font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${isActive ? "text-dcc" : "text-zinc-400"}`}>
              {m.properties} properties
            </span>
          </Link>
        );
      })}
    </div>
  );
}

// Extended-footprint state chips with owner-verified property counts.
export function ExtendedFootprint({ dark = true, testid = "extended-footprint" }) {
  return (
    <div className="mt-10">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">
        Extended footprint / {EXTENDED_STATES.length} more states
      </p>
      <div className="mt-4 flex flex-wrap gap-2" data-testid={testid}>
        {EXTENDED_STATES.map((s) => (
          <span key={s.state} className={`border px-3 py-2 font-mono text-xs uppercase tracking-[0.15em] ${dark ? "border-zinc-800 text-zinc-400" : "border-zinc-200 text-zinc-600"}`}>
            {s.state} · {s.count}
          </span>
        ))}
      </div>
    </div>
  );
}

// State-by-state accordion covering every operating market — the accessible
// city-list fallback for the map; selecting a city drives the floating card.
export function RegionAccordion({ activeId, onSelect, dark = true }) {
  const [openRegion, setOpenRegion] = useState(null);
  return (
    <div className="md:columns-2 md:gap-12" data-testid="region-list">
      {REGIONS.map((r, ri) => {
        const open = openRegion === r.state;
        return (
          <div key={r.state} className={`break-inside-avoid border-b ${dark ? "border-zinc-800" : "border-zinc-200"}`}>
            <button
              type="button"
              onClick={() => setOpenRegion(open ? null : r.state)}
              aria-expanded={open}
              aria-controls={`region-panel-${ri}`}
              data-testid={`region-toggle-${r.state.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`flex w-full cursor-pointer items-center justify-between py-4 text-left font-display text-base font-medium uppercase tracking-tight transition-colors hover:text-dcc ${dark ? "text-zinc-100" : "text-charcoal"}`}
            >
              <span>{r.state}</span>
              <span className={`font-mono text-xs text-zinc-400 transition-transform ${open ? "rotate-45" : ""}`} aria-hidden="true">+</span>
            </button>
            {open && (
              <ul id={`region-panel-${ri}`} className="pb-4">
                {r.cities.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => { onSelect({ ...c, state: r.state }); track("location_selected", { location: c.id, placement: "region_list" }); }}
                      data-testid={`region-city-${c.id}`}
                      aria-label={`Show ${c.name} on the map`}
                      className={`flex items-center gap-3 py-1.5 text-sm transition-colors hover:text-dcc ${activeId === c.id ? (dark ? "text-dcc" : "text-dcc-strong") : dark ? "text-zinc-400" : "text-zinc-600"}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${PRIORITY_MARKETS.includes(c.id) ? "bg-dcc" : dark ? "bg-zinc-700" : "bg-zinc-300"}`} aria-hidden="true" />
                      {c.name}
                      {PRIORITY_MARKETS.includes(c.id) && <span className={`font-mono text-xs uppercase tracking-widest ${dark ? "text-dcc" : "text-dcc-strong"}`}>Priority</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
